"use client";

import { useEffect, useState } from "react";
import { useUserStore } from "@/stores/userStore";
import { useRoleStore } from "@/stores/roleStore";
import { UpdateUserDto } from "@/types/user";
import { showToast } from "@/lib/toast";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ModalEditUserProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  onComplete: () => void;
}

export default function ModalEditUser({
  isOpen,
  onClose,
  userId,
  onComplete,
}: ModalEditUserProps) {
  const {
    selectedUser,
    isLoadingDetail,
    isUpdating,
    fetchUserById,
    updateUser,
    clearSelectedUser,
  } = useUserStore();
  const { roles, fetchRoles } = useRoleStore();

  const [formData, setFormData] = useState<UpdateUserDto>({
    username: "",
    name: "",
    email: "",
    role: "",
  });

  const [errors, setErrors] = useState<Partial<UpdateUserDto>>({});

  useEffect(() => {
    if (isOpen) {
      if (userId) fetchUserById(userId);
      fetchRoles();
    }
    return () => {
      clearSelectedUser();
    };
  }, [isOpen, userId, fetchUserById, fetchRoles, clearSelectedUser]);

  useEffect(() => {
    if (selectedUser) {
      setFormData({
        username: selectedUser.username || "",
        name: selectedUser.name || "",
        email: selectedUser.email || "",
        role:
          typeof selectedUser.role === "object"
            ? selectedUser.role?._id || ""
            : selectedUser.role || "",
      });
      setErrors({});
    }
  }, [selectedUser]);

  const validateForm = (): boolean => {
    const newErrors: Partial<UpdateUserDto> = {};

    if (!formData.username?.trim()) {
      newErrors.username = "Tên đăng nhập là bắt buộc";
    }

    if (!formData.name?.trim()) {
      newErrors.name = "Họ và tên là bắt buộc";
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email || "")) {
      newErrors.email = "Email không hợp lệ";
    }

    if (!formData.role) {
      newErrors.role = "Vai trò là bắt buộc";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !userId) {
      return;
    }

    try {
      const success = await updateUser(userId, formData);

      if (success) {
        showToast.success("Thành công", "Cập nhật người dùng thành công");
        onComplete();
        onClose();
      } else {
        showToast.error("Lỗi", "Không thể cập nhật người dùng");
      }
    } catch (error) {
      showToast.error("Lỗi", "Có lỗi xảy ra khi cập nhật người dùng");
    }
  };

  const handleInputChange = (field: keyof UpdateUserDto, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (isLoadingDetail) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">
              Đang tải...
            </span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!selectedUser) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="p-6 text-center text-gray-600 dark:text-gray-400">
            Không tìm thấy người dùng
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Chỉnh sửa người dùng
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div className="space-y-2">
            <Label
              htmlFor="username"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Tên đăng nhập <span className="text-red-500">*</span>
            </Label>
            <Input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              className={errors.username ? "border-red-500" : ""}
              placeholder="Nhập tên đăng nhập"
            />
            {errors.username && (
              <p className="text-red-500 text-sm">{errors.username}</p>
            )}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label
              htmlFor="name"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Họ và tên <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={errors.name ? "border-red-500" : ""}
              placeholder="Nhập họ và tên"
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={errors.email ? "border-red-500" : ""}
              placeholder="Nhập email"
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Vai trò <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange("role", value)}
            >
              <SelectTrigger className={errors.role ? "border-red-500" : ""}>
                <SelectValue placeholder="Chọn vai trò" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role._id} value={role._id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.role && (
              <p className="text-red-500 text-sm">{errors.role}</p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isUpdating}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? "Đang cập nhật..." : "Cập nhật người dùng"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
